import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Settings, LogOut, Camera, Bike, Sunrise, Mountain as MountainIcon,
  CalendarDays, ChevronRight, Award, Zap, Shield, User as UserIcon, Star, Trash2,
  ShoppingBag, KeyRound, Search,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useIsAdmin } from "@/lib/useIsAdmin";
import { BottomNav } from "@/components/BottomNav";
import { MODALITIES, relativeMinutes, type Modality } from "@/lib/modality";
import { WattsLogo } from "@/components/WattsLogo";
import i18n, { LANGUAGES } from "@/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useServerFn } from "@tanstack/react-start";
import { deleteMyAccount } from "@/lib/account.functions";
import { sendPasswordChangedEmail } from "@/lib/email.functions";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
});

interface Profile {
  nickname: string;
  avatar_url: string | null;
  preferred_modality: Modality | null;
  preferred_pace: string | null;
  rides_completed: number;
  language: string | null;
  watts_points: number | null;
}
interface RecentRide { id: string; modality: Modality; start_time: string; estimated_km: number | null; start_address: string | null; }
interface WattsTx { id: string; amount: number; reason: string; created_at: string; }

function reasonLabel(t: (k: string, opts?: any) => string, reason: string): string {
  return t(`profile.wattsPoints.reasons.${reason}`, { defaultValue: reason });
}

function timeAgo(t: (k: string, opts?: any) => string, iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return t("common.now");
  if (diff < 3600) return t("common.minAgo", { n: Math.floor(diff / 60) });
  if (diff < 86400) return t("common.hAgo", { n: Math.floor(diff / 3600) });
  return t("common.dAgo", { n: Math.floor(diff / 86400) });
}

type Role = "admin" | "ambassador" | "rider";

function ProfilePage() {
  const { t } = useTranslation();
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  const [confirmOut, setConfirmOut] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteEmailInput, setDeleteEmailInput] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(true);
  const [howToOpen, setHowToOpen] = useState(false);
  const [changePwdOpen, setChangePwdOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);
  const [changeNickOpen, setChangeNickOpen] = useState(false);
  const [nickInput, setNickInput] = useState("");
  const [nickCheck, setNickCheck] = useState<"idle" | "checking" | "available" | "taken" | "invalid">("idle");
  const [nickChangeUsed, setNickChangeUsed] = useState(false);
  const [nickPending, setNickPending] = useState<{ requested_nickname: string } | null>(null);
  const [submittingNick, setSubmittingNick] = useState(false);
  const deleteAccount = useServerFn(deleteMyAccount);
  const notifyPasswordChanged = useServerFn(sendPasswordChangedEmail);
  const doSignOut = async () => {
    await supabase.auth.signOut().catch(() => {});
    await signOut().catch(() => {});
    navigate({ to: "/auth", replace: true });
  };
  const doDeleteAccount = async () => {
    setDeleting(true);
    try {
      await deleteAccount({ data: {} } as any);
      await supabase.auth.signOut().catch(() => {});
      await signOut().catch(() => {});
      toast.success(t("profile.deleteAccountSuccess"));
      navigate({ to: "/auth", replace: true });
    } catch (e: any) {
      toast.error(e?.message || t("profile.deleteAccountError"));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
      setDeleteEmailInput("");
    }
  };
  const doChangePassword = async () => {
    if (!user?.email) return;
    if (newPwd.length < 8) { toast.error("Mínimo 8 caracteres."); return; }
    if (newPwd === currentPwd) { toast.error("La nueva contraseña debe ser distinta."); return; }
    if (newPwd !== confirmPwd) { toast.error("Las contraseñas no coinciden."); return; }
    setChangingPwd(true);
    try {
      // Validate current password via silent re-login
      const { error: signErr } = await supabase.auth.signInWithPassword({
        email: user.email, password: currentPwd,
      });
      if (signErr) throw new Error("Contraseña actual incorrecta.");
      const { error: updErr } = await supabase.auth.updateUser({ password: newPwd });
      if (updErr) throw updErr;
      // Best-effort confirmation email
      notifyPasswordChanged({ data: {} } as any).catch(() => {});
      toast.success("Contraseña actualizada");
      setChangePwdOpen(false);
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e: any) {
      toast.error(e?.message || "No se pudo actualizar la contraseña.");
    } finally {
      setChangingPwd(false);
    }
  };

  const [profile, setProfile] = useState<Profile | null>(null);
  const [stats, setStats] = useState({ created: 0, joined: 0, following: 0, followers: 0 });
  const [recent, setRecent] = useState<RecentRide[]>([]);
  const [transactions, setTransactions] = useState<WattsTx[]>([]);
  const [isAmbassador, setIsAmbassador] = useState(false);
  const [followSheetOpen, setFollowSheetOpen] = useState(false);
  const [followSheetType, setFollowSheetType] = useState<"following" | "followers">("following");
  const [followList, setFollowList] = useState<Array<{ id: string; nickname: string; avatar_url: string | null; ambassador_since: string | null }>>([]);

  // Debounced nickname availability check (excludes current nickname).
  useEffect(() => {
    if (!changeNickOpen) return;
    const trimmed = nickInput.trim();
    if (!trimmed) { setNickCheck("idle"); return; }
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(trimmed)) { setNickCheck("invalid"); return; }
    if (profile && trimmed.toLowerCase() === profile.nickname.toLowerCase()) {
      setNickCheck("taken"); return;
    }
    setNickCheck("checking");
    const h = setTimeout(async () => {
      const { data, error } = await supabase.rpc("nickname_exists" as any, { _nickname: trimmed });
      if (error) { setNickCheck("idle"); return; }
      setNickCheck(data ? "taken" : "available");
    }, 400);
    return () => clearTimeout(h);
  }, [nickInput, changeNickOpen, profile]);

  const submitNicknameRequest = async () => {
    if (!user || !profile) return;
    const requested = nickInput.trim();
    if (nickCheck !== "available") return;
    setSubmittingNick(true);
    const { error } = await supabase.from("nickname_requests" as any).insert({
      user_id: user.id,
      current_nickname: profile.nickname,
      requested_nickname: requested,
    });
    setSubmittingNick(false);
    if (error) { toast.error("No se pudo enviar la solicitud."); return; }
    toast.success("Solicitud enviada. Te avisaremos cuando se apruebe.");
    setNickPending({ requested_nickname: requested });
    setChangeNickOpen(false);
    setNickInput(""); setNickCheck("idle");
  };


  useEffect(() => { if (!loading && !user) navigate({ to: "/auth" }); }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data: p } = await supabase.from("profiles")
        .select("nickname, avatar_url, preferred_modality, preferred_pace, rides_completed, language, watts_points, ambassador_since, nickname_change_used")
        .eq("id", user.id).single();
      if (p) {
        setProfile(p as Profile);
        if (p.language && p.language !== i18n.language) i18n.changeLanguage(p.language);
        setIsAmbassador(!!(p as any).ambassador_since);
        setNickChangeUsed(!!(p as any).nickname_change_used);
      }

      const { data: pendingReq } = await supabase
        .from("nickname_requests" as any)
        .select("requested_nickname")
        .eq("user_id", user.id).eq("status", "pending")
        .maybeSingle();
      setNickPending(pendingReq ? (pendingReq as any) : null);

      const [{ count: following }, { count: followers }, { data: creatorRides }, { data: participantRows }] = await Promise.all([
        supabase.from("follows").select("*", { count: "exact", head: true }).eq("follower_id", user.id),
        supabase.from("follows").select("*", { count: "exact", head: true }).eq("following_id", user.id),
        supabase.from("rides").select("id").eq("creator_id", user.id),
        supabase.from("ride_participants").select("ride_id").eq("user_id", user.id),
      ]);
      const creatorRideIds = new Set((creatorRides || []).map((r: any) => r.id));
      const joinedCount = (participantRows || []).filter((p: any) => !creatorRideIds.has(p.ride_id)).length;
      setStats({
        created: (creatorRides || []).length,
        joined: joinedCount,
        following: following ?? 0,
        followers: followers ?? 0,
      });

      const { data: rs } = await supabase.from("rides")
        .select("id, modality, start_time, estimated_km, start_address")
        .eq("creator_id", user.id)
        .order("start_time", { ascending: false }).limit(5);
      setRecent((rs as RecentRide[]) || []);

      const { data: tx } = await supabase.from("watts_transactions")
        .select("id, amount, reason, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false }).limit(5);
      setTransactions((tx as WattsTx[]) || []);
    })();
  }, [user]);

  // Auto-request Embajador when reaching 100 Watts
  useEffect(() => {
    if (!user || !profile) return;
    const pts = profile.watts_points ?? 0;
    if (pts < 100) return;
    if (isAmbassador || isAdmin) return;
    const flagKey = `watts_ambassador_requested_${user.id}`;
    if (typeof window !== "undefined" && localStorage.getItem(flagKey)) return;
    (async () => {
      const { data: existing } = await supabase
        .from("ambassador_requests")
        .select("id, status")
        .eq("user_id", user.id)
        .in("status", ["pending", "approved"])
        .maybeSingle();
      if (existing) {
        try { localStorage.setItem(flagKey, "1"); } catch {}
        return;
      }
      const { error } = await supabase
        .from("ambassador_requests")
        .insert({ user_id: user.id, status: "pending" });
      if (!error) {
        try { localStorage.setItem(flagKey, "1"); } catch {}
        toast.success(t("profile.wattsPoints.ambassadorReached"));
      }
    })();
  }, [user, profile, isAmbassador, isAdmin]);

  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = async (file: File) => {
    if (!user) return;
    const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];
    const EXT_BY_MIME: Record<string, string> = {
      "image/jpeg": "jpg",
      "image/png": "png",
      "image/webp": "webp",
      "image/gif": "gif",
    };
    const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
    if (!ALLOWED.includes(file.type)) {
      toast.error(t("profile.avatarFormatError"));
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error(t("profile.avatarSizeError"));
      return;
    }
    setUploading(true);
    try {
      const ext = EXT_BY_MIME[file.type];
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from("avatars").upload(path, file, {
        upsert: true,
        contentType: file.type,
      });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
      const { error: updErr } = await supabase.from("profiles").update({ avatar_url: publicUrl }).eq("id", user.id);
      if (updErr) throw updErr;
      setProfile((p) => p ? { ...p, avatar_url: publicUrl } : p);
      toast.success(t("profile.avatarUpdated"));
    } catch (err: any) {
      console.error("[avatar upload]", err);
      toast.error(err?.message || err?.error || t("profile.avatarUploadError"));
    } finally { setUploading(false); }
  };

  const changeLanguage = async (lang: string) => {
    await i18n.changeLanguage(lang);
    try { localStorage.setItem("watts_lang", lang); } catch {}
    if (user) {
      await supabase.from("profiles").update({ language: lang }).eq("id", user.id);
      setProfile((p) => p ? { ...p, language: lang } : p);
    }
  };

  const loadFollowList = async (type: "following" | "followers") => {
    if (!user) return;
    const col = type === "following" ? "follower_id" : "following_id";
    const targetCol = type === "following" ? "following_id" : "follower_id";
    const { data } = await supabase.from("follows").select(targetCol).eq(col, user.id);
    if (!data || data.length === 0) {
      setFollowList([]);
      return;
    }
    const ids = data.map((d: any) => d[targetCol]);
    const { data: profiles } = await supabase.from("profiles").select("id, nickname, avatar_url, ambassador_since").in("id", ids);
    setFollowList((profiles as any[]) || []);
  };

  const openFollowSheet = (type: "following" | "followers") => {
    setFollowSheetType(type);
    setFollowSheetOpen(true);
    loadFollowList(type);
  };

  if (!profile) {
    return (
      <div className="min-h-screen bg-background p-5 pb-28 space-y-5">
        <div className="h-8 w-24 animate-pulse rounded-full bg-surface-elevated" />
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 animate-pulse rounded-full bg-surface-elevated" />
          <div className="flex-1 space-y-2">
            <div className="h-8 w-40 animate-pulse rounded-xl bg-surface-elevated" />
            <div className="h-5 w-32 animate-pulse rounded-full bg-surface-elevated" />
          </div>
        </div>
        <div className="h-20 w-full animate-pulse rounded-2xl bg-surface-elevated" />
        <div className="h-24 w-full animate-pulse rounded-2xl bg-surface-elevated" />
      </div>
    );
  }

  const mLabel = MODALITIES.find((m) => m.value === profile.preferred_modality)?.label;
  const currentLang = (profile.language ?? i18n.language ?? "es").slice(0, 2);
  const role: Role = isAdmin ? "admin" : isAmbassador ? "ambassador" : "rider";

  const SettingsMenu = (
    <Popover>
      <PopoverTrigger asChild>
        <button className="rounded-full bg-surface p-2" aria-label="Ajustes">
          <Settings className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[220px] p-0 bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl shadow-xl"
      >
        <div className="p-3 border-b border-[#2a2a2a]">
          <p className="text-[11px] text-[#888] mb-2 uppercase tracking-wider">{t("profile.language")}</p>
          <div className="flex gap-1.5">
            {LANGUAGES.map((l) => {
              const active = currentLang === l.code;
              return (
                <button
                  key={l.code}
                  onClick={() => changeLanguage(l.code)}
                  className={`flex-1 rounded-full px-2 py-1.5 text-[11px] font-medium transition-colors ${
                    active ? "bg-[#F5C518] text-black font-bold" : "bg-[#222] text-white border border-[#2a2a2a]"
                  }`}
                >
                  {l.display}
                </button>
              );
            })}
          </div>
        </div>
        <div className="p-3 border-b border-[#2a2a2a] flex justify-between items-center">
          <span className="text-sm text-white">{t("profile.notifications")}</span>
          <Switch checked={notifEnabled} onCheckedChange={setNotifEnabled} />
        </div>
        <div className="p-3 border-b border-[#2a2a2a]">
          <button
            onClick={() => setChangePwdOpen(true)}
            className="flex w-full items-center gap-2 text-white/80 text-sm hover:text-white"
          >
            <KeyRound size={14} /> Cambiar contraseña
          </button>
        </div>
        <div className="p-3 border-b border-[#2a2a2a]">
          <button
            onClick={() => setChangeNickOpen(true)}
            className="flex w-full items-center gap-2 text-white/80 text-sm hover:text-white"
          >
            <UserIcon size={14} /> Cambiar nombre de usuario
          </button>
        </div>
        <div className="p-3 border-b border-[#2a2a2a]">
          <button
            onClick={() => setConfirmOut(true)}
            className="flex w-full items-center gap-2 text-white/80 text-sm hover:text-white"
          >
            <LogOut size={14} /> {t("profile.signOut")}
          </button>
        </div>
        <div className="px-3 pt-3 pb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-red-500/80">
            {t("profile.dangerZone")}
          </span>
        </div>
        <div className="p-3">
          <button
            onClick={() => { setDeleteEmailInput(""); setConfirmDelete(true); }}
            className="flex w-full items-center gap-2 text-red-500 text-sm hover:text-red-400"
          >
            <Trash2 size={14} /> {t("profile.deleteAccount")}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );

  const RoleBadge = role === "rider" ? null : (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        role === "admin"
          ? "bg-[#F5C518]/20 text-[#F5C518] border-[#F5C518]/50"
          : "bg-[#F5C518]/10 text-[#F5C518] border-[#F5C518]/30"
      }`}
    >
      {role === "admin" ? <><Shield className="h-3 w-3" /> {t("profile.myRole.admin")}</> : <>⭐ {t("profile.myRole.ambassador")}</>}
    </span>
  );

  return (
    <div className="overflow-y-auto bg-background fade-in" style={{ height: '100dvh', paddingBottom: 80 }}>
      {/* Mobile header */}
      <header className="md:hidden relative flex flex-col items-center px-5" style={{ paddingTop: 'max(env(safe-area-inset-top), 12px)', paddingBottom: 8 }}>
        <WattsLogo width={70} />
        <div className="absolute right-5 top-[max(env(safe-area-inset-top),0.75rem)]">{SettingsMenu}</div>
      </header>

      {/* Desktop / tablet header */}
      <header
        className="hidden md:grid items-center px-6"
        style={{ gridTemplateColumns: '100px 1fr auto', paddingTop: 'max(env(safe-area-inset-top), 20px)', paddingBottom: 16, gap: 16 }}
      >
        <div><WattsLogo width={100} /></div>
        <div className="flex items-center justify-center" style={{ gap: 16 }}>
          <AvatarBlock profile={profile} uploading={uploading} onClick={() => fileInput.current?.click()} size="lg" />
          <div className="flex flex-col justify-center min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-display text-3xl truncate">@{profile.nickname}</div>
              {RoleBadge}
            </div>
            <div className="mt-1 flex flex-wrap gap-2">
              {mLabel && (
                <span className="rounded-full bg-surface-elevated px-2.5 py-1 text-xs font-medium">{mLabel}</span>
              )}
              {profile.preferred_pace && (
                <span className="rounded-full bg-surface-elevated px-2.5 py-1 text-xs font-medium capitalize">{profile.preferred_pace}</span>
              )}
            </div>
          </div>
        </div>
        <div className="self-start">{SettingsMenu}</div>
      </header>

      <div className="px-5 md:px-6">
        {/* Mobile-only avatar + nickname row */}
        <div className="md:hidden flex items-center" style={{ gap: 16 }}>
          <AvatarBlock profile={profile} uploading={uploading} onClick={() => fileInput.current?.click()} size="sm" />
          <div className="flex flex-col justify-center min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <div className="font-display text-[18px] truncate">@{profile.nickname}</div>
              {RoleBadge}
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {mLabel && (
                <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-[11px] font-medium">{mLabel}</span>
              )}
              {profile.preferred_pace && (
                <span className="rounded-full bg-surface-elevated px-2 py-0.5 text-[11px] font-medium capitalize">{profile.preferred_pace}</span>
              )}
            </div>
          </div>
        </div>

        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadAvatar(f); e.target.value = ""; }}
        />

        {/* Stats row */}
        <div className="grid grid-cols-[1fr_1px_1fr_1px_1fr_1px_1fr] rounded-2xl border border-border bg-surface" style={{ marginTop: 16 }}>
          <Stat n={stats.created} label={t("profile.created")} />
          <div className="h-8 w-px bg-[#2a2a2a] self-center" />
          <Stat n={stats.joined} label={t("profile.joined")} />
          <div className="h-8 w-px bg-[#2a2a2a] self-center" />
          <Stat n={stats.following} label={t("profile.following")} onClick={() => openFollowSheet("following")} />
          <div className="h-8 w-px bg-[#2a2a2a] self-center" />
          <Stat n={stats.followers} label={t("profile.followers")} onClick={() => openFollowSheet("followers")} />
        </div>

        {/* Widgets */}
        <div className="mt-6 space-y-3">
          {/* Widget 1: Find Riders */}
          <Widget icon={<Search className="h-4 w-4 text-[#F5C518]" />} title={t("profile.findRiders.title", { defaultValue: "Encuentra Riders" })}>
            <FindRidersSection currentUserId={user!.id} />
          </Widget>


          {/* Widget 2: Recent rides */}
          <Widget icon={<CalendarDays className="h-4 w-4 text-[#F5C518]" />} title={t("profile.recentRidesTitle")}>
            {recent.length === 0 ? (
              <p className="py-2 text-center text-sm text-white/60">{t("profile.noRides")}</p>
            ) : (
              <div className="space-y-2">
                {recent.slice(0, 3).map((r) => {
                  const m = MODALITIES.find((mm) => mm.value === r.modality);
                  const mColor = r.modality === "road" ? "#F5C518" : r.modality === "gravel" ? "#e07b3a" : "#4caf7d";
                  return (
                    <Link
                      key={r.id} to="/ride/$rideId" params={{ rideId: r.id }}
                      className="flex items-center justify-between rounded-xl bg-[#222] px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                            style={{ background: `color-mix(in oklab, ${mColor} 22%, transparent)`, color: mColor }}
                          >{m?.label}</span>
                          <span className="truncate text-xs text-white/70">{r.start_address?.split(",")[0] ?? "—"}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-muted-foreground">{relativeMinutes(r.start_time)}</div>
                      </div>
                      <div className="text-sm font-medium">{r.estimated_km ? `${r.estimated_km} km` : "—"}</div>
                    </Link>
                  );
                })}
                {recent.length > 3 && (
                  <button
                    onClick={() => navigate({ to: "/profile/rides" as any })}
                    className="w-full text-center text-xs text-[#F5C518] pt-1"
                  >
                    {t("profile.viewAll")}
                  </button>
                )}
              </div>
            )}
          </Widget>

          {/* Widget 3: Watts Points */}
          <Widget icon={<Zap className="h-4 w-4 text-[#F5C518]" />} title={t("profile.wattsPoints.title")}>
            {(() => {
              const pts = profile.watts_points ?? 0;
              const milestone = 100;
              const remaining = Math.max(0, milestone - pts);
              const reached = pts >= milestone;
              const progress = Math.min(100, (pts / milestone) * 100);
              const earnRows = [
                { label: t("profile.wattsPoints.earnRows.signup"), pts: 5 },
                { label: t("profile.wattsPoints.earnRows.create"), pts: 2 },
                { label: t("profile.wattsPoints.earnRows.join"), pts: 1 },
                { label: t("profile.wattsPoints.earnRows.5riders"), pts: 3 },
                { label: t("profile.wattsPoints.earnRows.10riders"), pts: 5 },
                { label: t("profile.wattsPoints.earnRows.refer"), pts: 2 },
                { label: t("profile.wattsPoints.earnRows.firstOfMonth"), pts: 2 },
              ];
              const benefits = [
                { icon: "⭐", label: t("profile.wattsPoints.benefits.badge") },
                { icon: "📍", label: t("profile.wattsPoints.benefits.propose") },
                { icon: "🗺️", label: t("profile.wattsPoints.benefits.featured") },
                { icon: "✅", label: t("profile.wattsPoints.benefits.verified") },
                { icon: "🎁", label: t("profile.wattsPoints.benefits.discounts") },
                { icon: "🚀", label: t("profile.wattsPoints.benefits.earlyAccess") },
                { icon: "👥", label: t("profile.wattsPoints.benefits.group") },
              ];
              return (
                <div>
                  <div className="text-center py-2">
                    <div
                      className="text-[#F5C518] tabular-nums"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 44, lineHeight: 1, fontWeight: 700 }}
                    >
                      {pts} Watts
                    </div>
                    <div className="mt-2 text-xs text-white/60">
                      {reached
                        ? t("profile.wattsPoints.levelAchieved")
                        : t("profile.wattsPoints.remaining", { n: remaining })}
                    </div>
                    <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#222]">
                      <div
                        className={`h-full transition-all ${reached ? "bg-emerald-500" : "bg-[#F5C518]"}`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="bg-[#111] rounded-xl p-3">
                      <p className="text-[#f5c518] text-xs font-bold uppercase tracking-wider mb-2">{t("profile.wattsPoints.howToEarn")}</p>
                      {earnRows.map((r) => (
                        <div key={r.label} className="flex justify-between items-center py-1">
                          <span className="text-white/60 text-xs">{r.label}</span>
                          <span className="text-[#f5c518] text-xs font-bold">+{r.pts}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#111] rounded-xl p-3">
                      <p className="text-[#f5c518] text-xs font-bold uppercase tracking-wider mb-2">{t("profile.wattsPoints.beAmbassador")}</p>
                      {benefits.map((b) => (
                        <div key={b.label} className="flex items-start gap-1 py-1">
                          <span className="text-xs">{b.icon}</span>
                          <span className="text-white/60 text-xs">{b.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {transactions.length > 0 && (
                    <div className="mt-3 space-y-1.5 border-t border-[#2a2a2a] pt-3">
                      {transactions.map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between text-xs">
                          <div className="min-w-0 flex-1 pr-2">
                            <span className="text-[#F5C518] font-semibold">+{tx.amount} Watts</span>
                            <span className="text-white/60"> — {reasonLabel(t, tx.reason)}</span>
                          </div>
                          <span className="text-white/40 shrink-0">{timeAgo(t, tx.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </Widget>


          {/* Widget 4: My role */}
          <Widget icon={<Star className="h-4 w-4 text-[#F5C518]" />} title={t("profile.myRole.title")}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#222]">
                {role === "admin" ? (
                  <Shield className="h-5 w-5 text-[#F5C518]" />
                ) : role === "ambassador" ? (
                  <Star className="h-5 w-5 text-[#F5C518]" />
                ) : (
                  <UserIcon className="h-5 w-5 text-white" />
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">
                  {role === "admin" ? t("profile.myRole.admin") : role === "ambassador" ? t("profile.myRole.ambassador") : t("profile.myRole.rider")}
                </div>
                <div className="text-[11px] text-white/60">
                  {role === "admin"
                    ? t("profile.myRole.adminDesc")
                    : role === "ambassador"
                    ? t("profile.myRole.ambassadorDesc")
                    : t("profile.myRole.riderDesc")}
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#2a2a2a]">
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-[#F5C518]" />
                <span className="text-xs font-semibold uppercase tracking-wider text-white/70">{t("profile.badges.title")}</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { Icon: Bike, label: t("profile.badges.firstRide") },
                  { Icon: Sunrise, label: t("profile.badges.earlyBird") },
                  { Icon: MountainIcon, label: t("profile.badges.climber") },
                ].map((b) => (
                  <div key={b.label} className="rounded-xl bg-[#222] p-3 text-center">
                    <b.Icon className="mx-auto mb-1 h-5 w-5 text-primary" strokeWidth={1.8} />
                    <div className="text-[11px] text-white/80">{b.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Widget>


          {/* Widget 5: Retos */}
          <Widget
            icon={<MountainIcon className="h-4 w-4 text-[#F5C518]" />}
            title="Retos"
            onHeaderClick={() => navigate({ to: "/retos" })}
          >
            <p className="py-4 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#888" }}>
              {t("profile.partners.comingSoon")}
            </p>
          </Widget>

          {/* Widget 6: Tienda Watts */}
          <Widget icon={<ShoppingBag className="h-4 w-4 text-[#F5C518]" />} title={t("profile.shop.title")}>
            <p className="py-4 text-center" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: "#888" }}>
              {t("profile.shop.comingSoon")}
            </p>
          </Widget>
        </div>
      </div>

      <AlertDialog open={confirmOut} onOpenChange={setConfirmOut}>
        <AlertDialogContent className="bg-[#1a1a1a] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("profile.signOutConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>{t("profile.signOutConfirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-surface-elevated border-border">{t("profile.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doSignOut} className="bg-red-500/90 text-white hover:bg-red-500">{t("profile.exit")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmDelete}
        onOpenChange={(open) => { setConfirmDelete(open); if (!open) setDeleteEmailInput(""); }}
      >
        <AlertDialogContent className="bg-[#1a1a1a] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("profile.deleteAccountTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("profile.deleteAccountBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2 space-y-2">
            <label className="block text-[12px] text-white/70">{t("profile.deleteAccountConfirmLabel")}</label>
            <input
              type="email"
              autoComplete="off"
              value={deleteEmailInput}
              onChange={(e) => setDeleteEmailInput(e.target.value)}
              placeholder={user?.email ?? t("profile.deleteAccountConfirmPlaceholder")}
              className="w-full rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2 text-sm text-white outline-none focus:border-red-500"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-surface-elevated border-border">{t("profile.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doDeleteAccount(); }}
              disabled={deleting || !user?.email || deleteEmailInput.trim().toLowerCase() !== user.email.toLowerCase()}
              className="bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting ? t("profile.deleteAccountDeleting") : t("profile.deleteAccountConfirmButton")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={howToOpen} onOpenChange={setHowToOpen}>
        <SheetContent side="bottom" className="z-[10000] bg-[#1a1a1a] border-t border-[#2a2a2a] rounded-t-2xl max-h-[82dvh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="font-display text-lg tracking-wider">{t("profile.wattsPoints.howToTitle")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 divide-y divide-[#2a2a2a]">
            {[
              { icon: "⚡", label: t("profile.wattsPoints.howRows.signup"), pts: 5, sub: null as string | null },
              { icon: "🚴", label: t("profile.wattsPoints.howRows.create"), pts: 2, sub: t("profile.wattsPoints.howRows.createSub") },
              { icon: "👥", label: t("profile.wattsPoints.howRows.join"), pts: 1, sub: null },
              { icon: "🤝", label: t("profile.wattsPoints.howRows.refer"), pts: 2, sub: null },
              { icon: "🌅", label: t("profile.wattsPoints.howRows.firstOfMonth"), pts: 2, sub: null },
              { icon: "🔥", label: t("profile.wattsPoints.howRows.5riders"), pts: 3, sub: null },
              { icon: "🏆", label: t("profile.wattsPoints.howRows.10riders"), pts: 5, sub: null },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between py-3">
                <span className="flex items-center gap-2 text-sm text-white">
                  <span className="text-lg">{r.icon}</span>
                  <span className="flex flex-col">
                    <span>{r.label}</span>
                    {r.sub && <span className="text-[11px] text-white/40">{r.sub}</span>}
                  </span>
                </span>
                <span className="text-sm font-semibold text-[#F5C518]">+{r.pts} Watts</span>
              </div>
            ))}
          </div>
          <SheetClose asChild>
            <button className="mt-5 w-full rounded-full bg-[#F5C518] py-3 text-sm font-semibold text-black">{t("map.close")}</button>
          </SheetClose>
          <div className="h-[calc(4rem+env(safe-area-inset-bottom))]" />
        </SheetContent>
      </Sheet>

      <Sheet open={followSheetOpen} onOpenChange={setFollowSheetOpen}>
        <SheetContent side="bottom" className="z-[10000] bg-[#1a1a1a] border-t border-[#2a2a2a] rounded-t-2xl">
          <SheetHeader>
            <SheetTitle className="font-display text-lg tracking-wider">{followSheetType === "following" ? t("profile.following") : t("profile.followers")}</SheetTitle>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {followList.length === 0 ? (
              <p className="text-center text-sm text-white/60 py-6">
                {followSheetType === "following"
                  ? t("profile.followSheet.noFollowing")
                  : t("profile.followSheet.noFollowers")}
              </p>
            ) : (
              followList.map((u) => (
                <Link
                  key={u.id}
                  to="/profile/$userId"
                  params={{ userId: u.id }}
                  onClick={() => setFollowSheetOpen(false)}
                  className="flex items-center gap-3 rounded-xl bg-[#222] p-3"
                >
                  <div className="h-10 w-10 shrink-1 overflow-hidden rounded-full bg-primary font-bold text-primary-foreground flex items-center justify-center text-sm">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      u.nickname[0]?.toUpperCase()
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">@{u.nickname}</div>
                    <div className="text-[11px] text-white/60">
                      {u.ambassador_since ? t("profile.myRole.ambassador") : t("profile.myRole.rider")}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-white/40 shrink-0" />
                </Link>
              ))
            )}
          </div>
          <SheetClose asChild>
            <button className="mt-5 w-full rounded-full bg-[#F5C518] py-3 text-sm font-semibold text-black">{t("map.close")}</button>
          </SheetClose>
        </SheetContent>
      </Sheet>

      <AlertDialog
        open={changePwdOpen}
        onOpenChange={(open) => {
          setChangePwdOpen(open);
          if (!open) { setCurrentPwd(""); setNewPwd(""); setConfirmPwd(""); }
        }}
      >
        <AlertDialogContent className="bg-[#1a1a1a] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Cambiar contraseña</AlertDialogTitle>
            <AlertDialogDescription>
              Introduce tu contraseña actual y elige una nueva.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2 space-y-2">
            <input
              type="password" autoComplete="current-password" placeholder="Contraseña actual"
              value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)}
              className="w-full rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2.5 text-sm text-white outline-none focus:border-[#F5C518]"
            />
            <input
              type="password" autoComplete="new-password" placeholder="Nueva contraseña (mín. 8)"
              value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
              className="w-full rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2.5 text-sm text-white outline-none focus:border-[#F5C518]"
            />
            <input
              type="password" autoComplete="new-password" placeholder="Confirmar nueva contraseña"
              value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)}
              className="w-full rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2.5 text-sm text-white outline-none focus:border-[#F5C518]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-surface-elevated border-border">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doChangePassword(); }}
              disabled={changingPwd || !currentPwd || !newPwd || !confirmPwd}
              className="bg-[#F5C518] text-black hover:bg-[#F5C518]/90 disabled:opacity-40"
            >
              {changingPwd ? "Guardando…" : "Guardar cambios"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={changeNickOpen}
        onOpenChange={(open) => {
          setChangeNickOpen(open);
          if (!open) { setNickInput(""); setNickCheck("idle"); }
        }}
      >
        <AlertDialogContent className="bg-[#1a1a1a] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Cambiar nombre de usuario</AlertDialogTitle>
            <AlertDialogDescription>
              {nickChangeUsed
                ? "Ya has usado tu cambio de nombre gratuito."
                : nickPending
                ? `Tienes una solicitud pendiente para @${nickPending.requested_nickname}.`
                : "Elige un nuevo nombre. Un admin revisará la solicitud antes de aplicarla. Solo puedes hacerlo una vez."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!nickChangeUsed && !nickPending && (
            <div className="mt-2 space-y-2">
              <input
                autoFocus
                type="text"
                inputMode="text"
                autoCapitalize="none"
                autoCorrect="off"
                placeholder="nuevo_nickname"
                value={nickInput}
                onChange={(e) => setNickInput(e.target.value)}
                className="w-full rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2.5 text-sm text-white outline-none focus:border-[#F5C518]"
              />
              <div className="text-xs h-4">
                {nickCheck === "checking" && <span className="text-white/50">Comprobando…</span>}
                {nickCheck === "available" && <span className="text-emerald-400">🟢 ¡Disponible!</span>}
                {nickCheck === "taken" && <span className="text-red-400">🔴 Ya está en uso, prueba otro</span>}
                {nickCheck === "invalid" && <span className="text-red-400">3–20 caracteres: letras, números, guion bajo</span>}
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-surface-elevated border-border">Cerrar</AlertDialogCancel>
            {!nickChangeUsed && !nickPending && (
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); submitNicknameRequest(); }}
                disabled={submittingNick || nickCheck !== "available"}
                className="bg-[#F5C518] text-black hover:bg-[#F5C518]/90 disabled:opacity-40"
              >
                {submittingNick ? "Enviando…" : "Solicitar cambio"}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      <BottomNav />
    </div>
  );
}

function AvatarBlock({
  profile, uploading, onClick, size,
}: { profile: Profile; uploading: boolean; onClick: () => void; size: "sm" | "lg" }) {
  const dim = size === "lg" ? "h-20 w-20 text-3xl" : "h-14 w-14 text-2xl";
  const badge = size === "lg" ? "h-7 w-7" : "h-5 w-5";
  const icon = size === "lg" ? "h-3.5 w-3.5" : "h-3 w-3";
  return (
    <div className="relative shrink-0">
      <button
        onClick={onClick}
        disabled={uploading}
        className={`relative ${dim} overflow-hidden rounded-full bg-primary font-bold text-primary-foreground ring-2 ring-primary/40`}
        aria-label="Cambiar avatar"
      >
        {profile.avatar_url ? (
          <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className="flex h-full w-full items-center justify-center">
            {profile.nickname[0]?.toUpperCase()}
          </span>
        )}
      </button>
      <button
        onClick={onClick}
        disabled={uploading}
        aria-label="Editar foto"
        className={`press-scale absolute -bottom-1 -right-1 flex ${badge} items-center justify-center rounded-full bg-[#F5C518] text-black shadow-[0_4px_12px_rgba(245,197,24,0.5)] ring-2 ring-[#0f0f0f]`}
      >
        <Camera className={icon} strokeWidth={2.4} />
      </button>
    </div>
  );
}

function Widget({
  icon, title, children, onHeaderClick,
}: { icon: React.ReactNode; title: string; children: React.ReactNode; onHeaderClick?: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-4">
      <button
        onClick={() => (onHeaderClick ? onHeaderClick() : setOpen((v) => !v))}
        className="flex w-full items-center justify-between"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-sm font-semibold">{title}</span>
        </div>
        <ChevronRight
          className={`h-4 w-4 text-white/50 transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

function Stat({ n, label, onClick }: { n: number; label: string; onClick?: () => void }) {
  const content = (
    <div className="p-3 md:p-4 text-center">
      <div className="font-display text-primary" style={{ fontSize: 20 }}>{n}</div>
      <div className="text-muted-foreground" style={{ fontSize: 10 }}>{label}</div>
    </div>
  );
  if (onClick) {
    return (
      <button onClick={onClick} className="w-full text-left">
        {content}
      </button>
    );
  }
  return content;
}

function FindRidersSection({ currentUserId }: { currentUserId: string }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Array<{ id: string; nickname: string; avatar_url: string | null }>>([]);
  const [following, setFollowing] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, nickname, avatar_url")
        .ilike("nickname", `%${q}%`)
        .neq("id", currentUserId)
        .limit(10);
      if (cancelled) return;
      const list = (data as any[]) || [];
      setResults(list);
      setSearched(true);
      if (list.length > 0) {
        const { data: f } = await supabase
          .from("follows")
          .select("following_id")
          .eq("follower_id", currentUserId)
          .in("following_id", list.map((u) => u.id));
        if (!cancelled) setFollowing(new Set((f as any[] || []).map((r) => r.following_id)));
      } else {
        setFollowing(new Set());
      }
      setLoading(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, currentUserId]);

  const toggleFollow = async (targetId: string) => {
    const isFollowing = following.has(targetId);
    const next = new Set(following);
    if (isFollowing) {
      next.delete(targetId);
      setFollowing(next);
      const { error } = await supabase
        .from("follows")
        .delete()
        .eq("follower_id", currentUserId)
        .eq("following_id", targetId);
      if (error) {
        next.add(targetId);
        setFollowing(new Set(next));
        toast.error("No se pudo actualizar");
      }
    } else {
      next.add(targetId);
      setFollowing(next);
      const { error } = await supabase
        .from("follows")
        .insert({ follower_id: currentUserId, following_id: targetId });
      if (error) {
        next.delete(targetId);
        setFollowing(new Set(next));
        toast.error("No se pudo actualizar");
      }
    }
  };

  return (
    <div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Busca por nickname..."
        className="w-full rounded-lg border border-[#2a2a2a] bg-[#0f0f0f] px-3 py-2.5 text-sm text-[#f5f5f5] outline-none focus:border-[#F5C518]"
      />
      <div className="mt-3 space-y-2">
        {loading && <p className="text-center text-xs text-white/40 py-2">Buscando...</p>}
        {!loading && searched && results.length === 0 && (
          <p className="text-center text-sm text-white/60 py-2">No se encontraron riders</p>
        )}
        {!loading && results.map((u) => {
          const isFollowing = following.has(u.id);
          return (
            <div key={u.id} className="flex items-center gap-3 rounded-xl bg-[#222] p-2.5">
              <Link
                to="/profile/$userId"
                params={{ userId: u.id }}
                className="flex items-center gap-3 flex-1 min-w-0"
              >
                <div className="h-9 w-9 shrink-0 overflow-hidden rounded-full bg-primary font-bold text-primary-foreground flex items-center justify-center text-sm">
                  {u.avatar_url ? (
                    <img src={u.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    u.nickname[0]?.toUpperCase()
                  )}
                </div>
                <div className="text-sm font-semibold truncate">@{u.nickname}</div>
              </Link>
              <button
                onClick={() => toggleFollow(u.id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isFollowing
                    ? "bg-[#222] text-white border border-[#2a2a2a]"
                    : "bg-[#F5C518] text-black"
                }`}
              >
                {isFollowing ? "Siguiendo" : "Seguir"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

